import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import Header from '../../components/common/Header';
import Sidebar from '../../components/common/Sidebar';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { FileText, Plus, Upload, HelpCircle, X } from 'lucide-react';
import './AdminPages.css';

const AdminPolicies = () => {
  const [policies, setPolicies] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showFaqModal, setShowFaqModal] = useState(false);

  const [policyForm, setPolicyForm] = useState({ heading: '', description: '', file: null });
  const [faqForm, setFaqForm] = useState({ question: '', order_number: 1, is_active: true });

  const { addToast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [polRes, faqRes] = await Promise.all([
        api.get('/company-policies/'),
        api.get('/company-policy-faqs/')
      ]);
      setPolicies(polRes.data.results || polRes.data);
      setFaqs(faqRes.data.results || faqRes.data);
    } catch (err) {
      console.error(err);
      addToast('Failed to load policy data.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadPolicy = async (e) => {
    e.preventDefault();
    if (!policyForm.file) {
      addToast('Please select a file to upload.', 'error');
      return;
    }

    const payload = new FormData();
    payload.append('heading', policyForm.heading);
    payload.append('description', policyForm.description);
    payload.append('document', policyForm.file);

    try {
      await api.post('/company-policies/', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      addToast('Company policy document uploaded and indexed into AI RAG!', 'success');
      setShowPolicyModal(false);
      setPolicyForm({ heading: '', description: '', file: null });
      fetchData();
    } catch (err) {
      console.error(err);
      addToast('Failed to upload policy document.', 'error');
    }
  };

  const handleSaveFaq = async (e) => {
    e.preventDefault();
    try {
      await api.post('/company-policy-faqs/', faqForm);
      addToast('Policy FAQ question created!', 'success');
      setShowFaqModal(false);
      setFaqForm({ question: '', order_number: 1, is_active: true });
      fetchData();
    } catch (err) {
      console.error(err);
      addToast('Failed to create FAQ.', 'error');
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <div style={{ display: 'flex', flex: 1 }}>
        <Sidebar />
        <main style={{ flex: 1, padding: '32px', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
            <div>
              <h1 style={{ fontSize: '1.8rem', fontWeight: 800 }}>Company Policy & FAQ Management</h1>
              <p style={{ color: '#64748b' }}>Upload official policy PDFs/DOCXs and order Frequently Asked Questions.</p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="btn btn-secondary" onClick={() => setShowFaqModal(true)}>
                <Plus size={16} /> Add Policy FAQ
              </button>
              <button className="btn btn-primary" onClick={() => setShowPolicyModal(true)}>
                <Upload size={16} /> Upload Policy Document
              </button>
            </div>
          </div>

          {loading ? <LoadingSpinner label="Loading policy management..." /> : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '28px' }}>
              
              {/* Policies List */}
              <div className="card" style={{ padding: '24px' }}>
                <h2 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FileText size={20} style={{ color: '#4f46e5' }} /> Uploaded Policy Files ({policies.length})
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {policies.map(p => (
                    <div key={p.id} style={{ padding: '16px 20px', background: 'rgba(15, 23, 42, 0.75)', borderRadius: '14px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <div style={{ fontWeight: 700, fontSize: '1rem', color: '#f8fafc' }}>{p.heading}</div>
                      <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginTop: '4px' }}>{p.description}</div>
                      <span className="badge badge-success" style={{ marginTop: '10px', fontSize: '0.7rem' }}>Indexed in RAG</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQs List */}
              <div className="card" style={{ padding: '24px' }}>
                <h2 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <HelpCircle size={20} style={{ color: '#059669' }} /> Ordered Policy FAQs ({faqs.length})
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {faqs.map(f => (
                    <div key={f.id} style={{ padding: '14px 18px', background: 'rgba(15, 23, 42, 0.75)', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)', display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <span style={{ fontWeight: 800, color: '#34d399', background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '4px 10px', borderRadius: '8px', fontSize: '0.82rem' }}>
                        #{f.order_number}
                      </span>
                      <span style={{ fontSize: '0.92rem', fontWeight: 600, color: '#f8fafc' }}>{f.question}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )}

          {/* Modal: Upload Policy */}
          {showPolicyModal && (
            <div className="modal-backdrop">
              <div className="modal-content" style={{ padding: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <h2 style={{ fontSize: '1.2rem', fontWeight: 700 }}>Upload Company Policy Document</h2>
                  <button onClick={() => setShowPolicyModal(false)}><X size={20} /></button>
                </div>
                <form onSubmit={handleUploadPolicy}>
                  <div className="form-group">
                    <label className="form-label">Policy Heading *</label>
                    <input type="text" className="form-input" required placeholder="e.g. Leave Policy 2026" value={policyForm.heading} onChange={e => setPolicyForm({...policyForm, heading: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <input type="text" className="form-input" placeholder="Brief summary" value={policyForm.description} onChange={e => setPolicyForm({...policyForm, description: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">File (PDF, DOCX, TXT) *</label>
                    <input type="file" className="form-input" required accept=".pdf,.docx,.txt" onChange={e => setPolicyForm({...policyForm, file: e.target.files[0]})} />
                  </div>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowPolicyModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Upload & Index Policy</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Modal: Add FAQ */}
          {showFaqModal && (
            <div className="modal-backdrop">
              <div className="modal-content" style={{ padding: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <h2 style={{ fontSize: '1.2rem', fontWeight: 700 }}>Add Policy FAQ Question</h2>
                  <button onClick={() => setShowFaqModal(false)}><X size={20} /></button>
                </div>
                <form onSubmit={handleSaveFaq}>
                  <div className="form-group">
                    <label className="form-label">FAQ Question *</label>
                    <input type="text" className="form-input" required placeholder="e.g. What is the annual leave policy?" value={faqForm.question} onChange={e => setFaqForm({...faqForm, question: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Order Number ASC *</label>
                    <input type="number" className="form-input" required min={1} value={faqForm.order_number} onChange={e => setFaqForm({...faqForm, order_number: parseInt(e.target.value)})} />
                  </div>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowFaqModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Save FAQ</button>
                  </div>
                </form>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
};

export default AdminPolicies;
